import { DestroyRef, Injectable, Injector, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiRuntimeConfig } from './api-runtime-config';
import { AuthSessionService } from './auth-session.service';

export type AgentSyncEntity = 'queue' | 'in-process' | 'parked' | 'event-detail' | 'patrol' | 'sites';

interface SyncRevisionResponse {
  events: number;
  inProcess: number;
  parked: number;
  patrol: number;
  sites: number;
  eventDetail: number;
  session: number;
  storeEpoch?: number;
}

const EMPTY_WATCHES: Record<AgentSyncEntity, number> = {
  queue: 0,
  'in-process': 0,
  parked: 0,
  'event-detail': 0,
  patrol: 0,
  sites: 0,
};

@Injectable({ providedIn: 'root' })
export class AgentDataSyncService {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly apiConfig = inject(ApiRuntimeConfig);
  private connected = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private last: SyncRevisionResponse | null = null;
  private readonly watchCount: Record<AgentSyncEntity, number> = { ...EMPTY_WATCHES };

  private readonly revisions = {
    queue: signal(0),
    'in-process': signal(0),
    parked: signal(0),
    'event-detail': signal(0),
    patrol: signal(0),
    sites: signal(0),
  };

  /** Snapshot of every entity. Prefer `revisionFor` in resource params so unused entities do not invalidate. */
  readonly entityRevision = computed(() => ({
    queue: this.revisions.queue(),
    'in-process': this.revisions['in-process'](),
    parked: this.revisions.parked(),
    'event-detail': this.revisions['event-detail'](),
    patrol: this.revisions.patrol(),
    sites: this.revisions.sites(),
  }));

  /** Reads only that entity's revision so a bump elsewhere does not retrigger resource params. */
  revisionFor(entity: AgentSyncEntity): number {
    return this.revisions[entity]();
  }

  /** Start watching an entity; stop when the UI that needs it is destroyed. */
  watch(entity: AgentSyncEntity): () => void {
    this.watchCount[entity] += 1;
    return () => {
      this.watchCount[entity] = Math.max(0, this.watchCount[entity] - 1);
    };
  }

  watchWhileAlive(entity: AgentSyncEntity, destroyRef = inject(DestroyRef)): void {
    destroyRef.onDestroy(this.watch(entity));
  }

  /**
   * Starts the revision feed. `/api/sync/revision` is an EBM-only endpoint, so in
   * `dev`/`prod` there is nothing to poll and tables refresh on their own interval.
   */
  connect(accessToken: string): void {
    if (!accessToken || this.connected || !this.apiConfig.mockApiAvailable()) {
      return;
    }

    this.connected = true;
    void this.poll();
    this.timer = setInterval(() => void this.poll(), 3000);
  }

  disconnect(): void {
    this.connected = false;
    this.last = null;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  notifyEntityChanged(entity: AgentSyncEntity | 'events'): void {
    if (entity === 'events') {
      this.bump('queue');
      this.bump('in-process');
      this.bump('parked');
      return;
    }
    this.bump(entity);
  }

  private bump(entity: AgentSyncEntity): void {
    this.revisions[entity].update((current) => current + 1);
  }

  private isWatched(entity: AgentSyncEntity): boolean {
    return this.watchCount[entity] > 0;
  }

  private async poll(): Promise<void> {
    try {
      const next = await firstValueFrom(this.http.get<SyncRevisionResponse>('/api/sync/revision'));
      const prev = this.last;
      this.last = next;
      if (!prev) {
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (this.isWatched('queue') && next.events !== prev.events) {
        this.bump('queue');
      }
      if (this.isWatched('in-process') && next.inProcess !== prev.inProcess) {
        this.bump('in-process');
      }
      if (this.isWatched('parked') && next.parked !== prev.parked) {
        this.bump('parked');
      }
      if (this.isWatched('patrol') && next.patrol !== prev.patrol) {
        this.bump('patrol');
      }
      if (this.isWatched('sites') && next.sites !== prev.sites) {
        this.bump('sites');
      }
      if (this.isWatched('event-detail') && next.eventDetail !== prev.eventDetail) {
        this.bump('event-detail');
      }
      const storeEpochChanged =
        typeof next.storeEpoch === 'number' &&
        typeof prev.storeEpoch === 'number' &&
        next.storeEpoch !== prev.storeEpoch;
      if (storeEpochChanged) {
        const auth = this.injector.get(AuthSessionService);
        if (auth.isAuthenticated()) {
          auth.handleMockStoreReset();
        }
        return;
      }
      if (next.session !== prev.session) {
        const auth = this.injector.get(AuthSessionService);
        if (auth.isAuthenticated()) {
          auth.handleSessionSuperseded();
        }
      }
    } catch {
      // Poll is best-effort while the mock backend is down; auth interceptor handles 401 kick.
    }
  }
}
